import {
  Children,
  FunctionComponentElement,
  cloneElement,
  isValidElement,
} from "react";
import { CarPdfBox, CarPdfBoxProps } from "./PdfBox";
import { CarPdfTypography, CarPdfTypographyProps } from "./PdfTypography";
import { formatPercentFactor, isEvenOdd } from "@app/utils";
import { useTheme } from "@mui/material";

interface InternalProps {
  columnWidths?: number[];
  paddingVertical?: number;
  paddingHorizontal?: number;
  textVariant?: CarPdfTypographyProps["variant"];
  isRounded?: boolean;
  isStriped?: boolean;
  isFirstColumn?: boolean;
  isLastColumn?: boolean;
  isLastRow?: boolean;
  columnIndex?: number;
  rowIndex?: number;
}

interface WithInternalProps {
  internalProps?: InternalProps;
}

const isTableElement = (
  value: unknown,
): value is FunctionComponentElement<WithInternalProps> =>
  isValidElement(value);

export interface CarPdfHeaderProps extends CarPdfBoxProps {
  textVariant?: CarPdfTypographyProps["variant"];
  internalProps?: InternalProps;
  paddingVertical?: number;
}

export const CarPdfHeader = ({
  textVariant,
  children,
  style,
  internalProps,
  paddingVertical,
  ...props
}: CarPdfHeaderProps) => {
  const flatChildren = Children.toArray(children).filter(isTableElement);

  return (
    <CarPdfBox
      style={{
        flexDirection: "row",
        ...style,
      }}
      fixed
      {...props}
    >
      {Children.map(flatChildren, (child, columnIndex) =>
        cloneElement<WithInternalProps>(child, {
          internalProps: {
            isFirstColumn: columnIndex === 0,
            isLastColumn: columnIndex === flatChildren.length - 1,
            columnIndex,
            textVariant,
            ...internalProps,
            paddingVertical: paddingVertical ?? internalProps?.paddingVertical,
          },
        }),
      )}
    </CarPdfBox>
  );
};

export interface CarPdfHeaderCellProps
  extends Omit<CarPdfTypographyProps, "variant"> {
  textVariant?: CarPdfTypographyProps["variant"];
  internalProps?: InternalProps;
}

export const CarPdfHeaderCell = ({
  textVariant,
  children,
  style,
  internalProps,
  ...props
}: CarPdfHeaderCellProps) => {
  const theme = useTheme();
  const columnWidth = internalProps?.columnWidths?.at(
    internalProps?.columnIndex ?? -1,
  );
  return (
    <CarPdfTypography
      variant={textVariant ?? internalProps?.textVariant ?? "tableHeading"}
      {...props}
      style={{
        borderLeftWidth: 1,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderRightWidth: internalProps?.isLastColumn ? 1 : 0,
        borderColor: theme.palette.gray6,
        backgroundColor: theme.palette.gray3,
        borderTopLeftRadius:
          internalProps?.isRounded && internalProps?.isFirstColumn ? 5 : 0,
        borderTopRightRadius:
          internalProps?.isRounded && internalProps?.isLastColumn ? 5 : 0,
        width: columnWidth ? formatPercentFactor(columnWidth, 1) : "100%",
        paddingHorizontal: 2,
        paddingVertical: internalProps?.paddingVertical ?? 2,
        textAlign: "center",
        ...style,
      }}
    >
      {children}
    </CarPdfTypography>
  );
};

export interface CarPdfRowProps extends CarPdfBoxProps {
  internalProps?: InternalProps;
  paddingVertical?: number;
  textVariant?: CarPdfTypographyProps["variant"];
}

export const CarPdfRow = ({
  children,
  style,
  paddingVertical,
  textVariant,
  internalProps,
  ...props
}: CarPdfRowProps) => {
  const flatChildren = Children.toArray(children).filter(isTableElement);

  return (
    <CarPdfBox
      style={{
        flexDirection: "row",
        ...style,
      }}
      wrap={false}
      {...props}
    >
      {Children.map(flatChildren, (child, columnIndex) =>
        cloneElement<WithInternalProps>(child, {
          internalProps: {
            isFirstColumn: columnIndex === 0,
            isLastColumn: columnIndex === flatChildren.length - 1,
            columnIndex,
            ...internalProps,
            paddingVertical: paddingVertical ?? internalProps?.paddingVertical,
            textVariant: textVariant ?? internalProps?.textVariant,
          },
        }),
      )}
    </CarPdfBox>
  );
};

export interface CarPdfTextCellProps
  extends Omit<CarPdfTypographyProps, "variant"> {
  internalProps?: InternalProps;
  textVariant?: CarPdfTypographyProps["variant"];
}

export const CarPdfTextCell = ({
  children,
  style,
  internalProps,
  textVariant,
  ...props
}: CarPdfTextCellProps) => {
  const theme = useTheme();

  const columnWidth = internalProps?.columnWidths?.at(
    internalProps?.columnIndex ?? -1,
  );

  return (
    <CarPdfTypography
      variant={textVariant ?? internalProps?.textVariant ?? "tableBody1"}
      style={{
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderRightWidth: internalProps?.isLastColumn ? 1 : 0,
        borderColor: theme.palette.gray6,
        overflow: "hidden",
        paddingVertical: internalProps?.paddingVertical ?? 4,
        paddingHorizontal: internalProps?.paddingHorizontal,
        textAlign: "center",
        width: columnWidth ? formatPercentFactor(columnWidth, 1) : "100%",
        borderBottomLeftRadius:
          internalProps?.isRounded &&
          internalProps?.isLastRow &&
          internalProps?.isFirstColumn
            ? 5
            : 0,
        borderBottomRightRadius:
          internalProps?.isRounded &&
          internalProps?.isLastRow &&
          internalProps?.isLastColumn
            ? 5
            : 0,
        backgroundColor:
          internalProps?.isStriped && isEvenOdd(internalProps?.rowIndex ?? 0)
            ? theme.palette.gray2
            : undefined,
        ...style,
      }}
      {...props}
    >
      {children}
    </CarPdfTypography>
  );
};

export interface CarPdfViewCellProps extends CarPdfBoxProps {
  internalProps?: InternalProps;
}

export const CarPdfViewCell = ({
  children,
  style,
  internalProps,
  ...props
}: CarPdfViewCellProps) => {
  const theme = useTheme();

  const columnWidth = internalProps?.columnWidths?.at(
    internalProps?.columnIndex ?? -1,
  );

  return (
    <CarPdfBox
      style={{
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderRightWidth: internalProps?.isLastColumn ? 1 : 0,
        borderColor: theme.palette.gray6,
        overflow: "hidden",
        paddingVertical: internalProps?.paddingVertical ?? 0,
        paddingHorizontal: internalProps?.paddingHorizontal,
        textAlign: "center",
        width: columnWidth ? formatPercentFactor(columnWidth, 1) : "100%",
        flexDirection: "row",
        borderBottomLeftRadius:
          internalProps?.isRounded &&
          internalProps?.isLastRow &&
          internalProps?.isFirstColumn
            ? 5
            : 0,
        borderBottomRightRadius:
          internalProps?.isRounded &&
          internalProps?.isLastRow &&
          internalProps?.isLastColumn
            ? 5
            : 0,
        backgroundColor:
          internalProps?.isStriped && isEvenOdd(internalProps?.rowIndex ?? 0)
            ? theme.palette.gray2
            : undefined,
        ...style,
      }}
      {...props}
    >
      {children}
    </CarPdfBox>
  );
};

export interface CarPdfTableProps extends CarPdfBoxProps {
  colWidths?: number[];
  isRounded?: boolean;
  isStriped?: boolean;
  paddingVertical?: number;
  paddingHorizontal?: number;
}

export const CarPdfTable = ({
  children,
  colWidths,
  isRounded,
  isStriped,
  paddingVertical,
  paddingHorizontal,
  ...props
}: CarPdfTableProps) => {
  const totalWidth = colWidths?.reduce((acc, i) => acc + i, 0);

  const convertedWidths = colWidths?.map((i) => i / (totalWidth ?? 1));

  const flatChildren = Children.toArray(children).filter(isTableElement);

  return (
    <CarPdfBox {...props}>
      {Children.map(flatChildren, (child, rowIndex) =>
        cloneElement<WithInternalProps>(child, {
          internalProps: {
            columnWidths: convertedWidths,
            paddingVertical,
            paddingHorizontal,
            isRounded,
            isStriped,
            rowIndex: rowIndex - 1, // first Children is header
            isLastRow: rowIndex === flatChildren.length - 1,
          },
        }),
      )}
    </CarPdfBox>
  );
};
